import { useState } from 'react';

import * as Sentry from '@sentry/browser';
import posthog from 'posthog-js';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import sanitize from 'sanitize-html';
import { toast } from 'sonner';

import RichTextEditor from '@/components/common/RichText/RichTextEditor';
import SubmitButton from '@/components/common/SubmitButton';
import SubPageHeader from '@/components/common/SubPageHeader';
import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import useAuth from '@/hooks/useAuth';

import useSelectedParty from '../analysis/hooks/useSelectedParty';

import useCreateNote from './mutations/useCreateNote';

export default function NoteCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [html, setHtml] = useState('');

  const { broker } = useAuth();
  const { email } = useEmail();

  const party = useSelectedParty();

  const { mutateAsync: createNote } = useCreateNote();

  const handleSubmit = async () => {
    await createNote(
      {
        text: sanitize(html),
        partyId: party?.id ?? '',
      },
      {
        onSuccess: () => {
          posthog.capture('Note created', {
            partyId: party?.id,
            messageId: email?.messageId,
            crmBackend: broker?.crm,
          });
          toast.success(t('NOTE_CREATED'));
        },
        onError: (error) => {
          toast.error(t('NOTE_CREATE_FAILED'));
          Sentry.captureException(error);
        },
      },
    );

    navigate(-1);
  };

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <SubPageHeader>
        <div className="flex w-full items-center justify-between">
          <span>{t('NOTE_DETAIL_CREATE_TITLE')}</span>
        </div>
      </SubPageHeader>

      <main className="flex min-h-0 flex-1 flex-col gap-2 p-2">
        <RichTextEditor
          className="max-h-96 min-h-0 flex-1"
          value={html}
          onChange={setHtml}
        />

        <div className="flex shrink-0 justify-end gap-2">
          <SubmitButton onSubmit={handleSubmit} />
        </div>
      </main>
    </div>
  );
}
